import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const WAVEFORM_OPTIONS = [
  'Triphasic',
  'Biphasic',
  'Monophasic',
  'Not Well Visualized'
];

const ARTERY_LABELS = {
  commonFemoralArtery: 'Common Femoral Artery',
  superficialFemoralArtery: 'Superficial Femoral Artery',
  poplitealArtery: 'Popliteal Artery',
  posteriorTibialArtery: 'Posterior Tibial Artery',
  anteriorTibialArtery: 'Anterior Tibial Artery',
  dorsalisPedisArtery: 'Dorsalis Pedis Artery'
};

const emptyArtery = () => ({ psv: '', waveform: 'Triphasic', notes: '' });

const emptyLeg = () => Object.keys(ARTERY_LABELS).reduce((acc, key) => {
  acc[key] = emptyArtery();
  return acc;
}, {});

function LegArterialForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Right Lower Extremity, 1=Left Lower Extremity
  const [activeTab, setActiveTab] = useState(0);
  const { technologists, defaultTech } = useTechnologists();

  const INITIAL_DATA = {
    examType: 'Lower Extremity Arterial',
    examDate: new Date().toISOString().split('T')[0],
    firstName: '',
    lastName: '',
    mrn: '',
    technologist: '',
    reason: '',
    callReport: false,
    phoneNumber: '',
    history: '',
    comments: '',
    rightLegEvaluated: false,
    leftLegEvaluated: false,
    rightLeg: emptyLeg(),
    leftLeg: emptyLeg()
  };

  const {
    formData,
    error,
    success,
    isLoading,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    updateField,
    resetForm,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(INITIAL_DATA, 'Lower Extremity Arterial', {
    token, facilityInfo
  });

  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  }, [hookHandleSubmit]);

  const handleArteryChange = (leg, artery, field, value) => {
    const currentLegData = formData[leg];
    const updatedLegData = {
      ...currentLegData,
      [artery]: {
        ...currentLegData[artery],
        [field]: value
      }
    };
    updateField(leg, updatedLegData);
  };

  const handleLegEvaluationChange = (leg, evaluated) => {
    updateField(`${leg}Evaluated`, evaluated);
  };

  const renderArteryRow = (leg, arteryKey, isEvaluated) => {
    const arteryData = formData[leg][arteryKey];
    const isAbnormal = arteryData.waveform === 'Monophasic';
    const needsNotes = isAbnormal || arteryData.waveform === 'Not Well Visualized';

    return (
      <div className="form-group" style={{ marginBottom: '15px', opacity: isEvaluated ? 1 : 0.5 }}>
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
          {ARTERY_LABELS[arteryKey]}:
        </label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '0 0 140px' }}>
            <input
              type="number"
              step="1"
              placeholder="PSV (cm/s)"
              value={arteryData.psv}
              onChange={(e) => handleArteryChange(leg, arteryKey, 'psv', e.target.value)}
              disabled={!isEvaluated}
            />
          </div>
          <div style={{ flex: '0 0 200px' }}>
            <select
              value={arteryData.waveform}
              onChange={(e) => handleArteryChange(leg, arteryKey, 'waveform', e.target.value)}
              disabled={!isEvaluated}
              style={{
                width: '100%',
                backgroundColor: !isEvaluated ? 'var(--color-surface)' : (isAbnormal ? 'var(--color-status-bad-soft)' : 'var(--color-input-bg)'),
                borderColor: isAbnormal ? 'var(--color-danger)' : 'var(--color-border)',
                cursor: isEvaluated ? 'pointer' : 'not-allowed'
              }}
            >
              {WAVEFORM_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          {needsNotes && isEvaluated && (
            <input
              type="text"
              value={arteryData.notes}
              onChange={(e) => handleArteryChange(leg, arteryKey, 'notes', e.target.value)}
              placeholder="Additional notes (e.g., plaque, stenosis)..."
              style={{ flex: '1 1 200px', minWidth: '180px' }}
            />
          )}
        </div>
      </div>
    );
  };

  const renderLegTab = (leg, legLabel, evaluated) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
        <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>{legLabel} Lower Extremity</h3>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
            <input
              type="radio"
              name={`${leg}Evaluation`}
              checked={evaluated}
              onChange={() => handleLegEvaluationChange(leg, true)}
            />
            <span style={{ fontWeight: evaluated ? 'bold' : 'normal', color: evaluated ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
              Evaluated
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
            <input
              type="radio"
              name={`${leg}Evaluation`}
              checked={!evaluated}
              onChange={() => handleLegEvaluationChange(leg, false)}
            />
            <span style={{ fontWeight: !evaluated ? 'bold' : 'normal', color: !evaluated ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
              Not Evaluated
            </span>
          </label>
        </div>
      </div>

      {Object.keys(ARTERY_LABELS).map(arteryKey => (
        <React.Fragment key={arteryKey}>
          {renderArteryRow(leg, arteryKey, evaluated)}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button type="button" className="btn-close" onClick={clearMessages} aria-label="Close" />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button type="button" className="btn-close" onClick={clearMessages} aria-label="Close" />
          </div>
        )}

        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history (e.g., claudication, rest pain, absent pulses)"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            {['Right Lower Extremity', 'Left Lower Extremity'].map((tabLabel, tabIndex) => (
              <button
                key={tabLabel}
                type="button"
                onClick={() => setActiveTab(tabIndex)}
                className={`worksheet-tab-btn${activeTab === tabIndex ? ' active' : ''}`}
              >
                {tabLabel}
              </button>
            ))}
          </div>

          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {activeTab === 0 && renderLegTab('rightLeg', 'Right', formData.rightLegEvaluated)}
            {activeTab === 1 && renderLegTab('leftLeg', 'Left', formData.leftLegEvaluated)}
          </div>
        </div>

        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments or impressions..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        <div className="form-actions">
          <button type="button" onClick={handlePreview} disabled={isLoading} className="preview-btn">
            Preview Report
          </button>
          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button type="button" onClick={resetForm} disabled={isLoading} className="reset-btn">
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default LegArterialForm;
